// SEAT-DRAWER-UX §2 (docs/design/2026-10-28-seat-drawer-ux.md): the row every drawer tab shares.
//
//   [dot] Name ······························  value / target  ▁▂▃▅▆  [chip]   ✎ 🗑
//
// The name is a button that opens the item's modal; nothing in a row navigates. The value's
// target is faint; the sparkline is the row's own trend (hidden under two points); the chip says
// the state the dot shows (never colour alone); the hover actions (edit, remove; always on touch)
// are drawn only for a viewer who may change the row. `below` hangs under the row (an attainment
// bar, a description, milestones).
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  Sparkline,
} from "@companyos/ui";
import { PlusIcon } from "lucide-react";
import type { ReactNode } from "react";
import { RowActions } from "../settings/row-actions";

export function ItemRow({
  dot,
  name,
  title,
  onOpen,
  slot,
  value,
  trend,
  chip,
  actions,
  below,
  item,
}: {
  dot?: ReactNode;
  name: ReactNode;
  /** The full name (the button's tooltip and the tests' handle). */
  title: string;
  onOpen: () => void;
  /** The name button's `data-slot`. */
  slot: string;
  value?: ReactNode;
  /** The row's own trend, oldest first: a sparkline from two points. */
  trend?: { values: (number | null)[]; reference?: (number | null)[]; label: string } | undefined;
  chip?: ReactNode;
  /** `RowAction`s, for a viewer who may change the row. */
  actions?: ReactNode;
  below?: ReactNode;
  /** The row's id, on the `li` (`data-item`). */
  item: string;
}) {
  const points = trend ? trend.values.filter((v) => v !== null).length : 0;
  return (
    <li data-item={item} className="group/row flex min-w-0 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-2 text-xs">
        {dot}
        <button
          type="button"
          className="min-w-0 flex-1 truncate rounded-sm text-left text-sm leading-5 text-secondary hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          title={title}
          data-slot={slot}
          onClick={onOpen}
        >
          {name}
        </button>
        {value !== undefined ? (
          <span className="shrink-0 tabular-nums" data-slot="row-value">
            {value}
          </span>
        ) : null}
        {trend && points >= 2 ? (
          <Sparkline
            values={trend.values}
            {...(trend.reference ? { reference: trend.reference } : {})}
            label={trend.label}
            className="h-4 w-14 shrink-0 text-(--lens)"
          />
        ) : null}
        {chip}
        {actions ? <RowActions className="shrink-0">{actions}</RowActions> : null}
      </div>
      {below}
    </li>
  );
}

/** The tab header's Add: always visible, for a viewer who may add. */
export function TabAdd({
  label,
  onClick,
  slot,
}: {
  label: string;
  onClick: () => void;
  slot?: string;
}) {
  return (
    <Button variant="outline" size="xs" onClick={onClick} data-slot={slot ?? "tab-add"}>
      <PlusIcon data-icon="inline-start" aria-hidden="true" />
      {label}
    </Button>
  );
}

/** A tab's empty state: one line saying what is missing, and the way to add it. */
export function TabEmpty({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-2" data-slot="tab-empty">
      <p className="text-xs text-faint">{children}</p>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** "+N more" under the All view's rows: switches the drawer to the tab. */
export function MoreRowsLink({ n, onClick }: { n: number; onClick: () => void }) {
  if (n <= 0) return null;
  return (
    <button
      type="button"
      className="self-start text-xs text-primary-text hover:underline"
      data-slot="tab-more"
      onClick={onClick}
    >
      +{n} more
    </button>
  );
}

/** A controlled confirm for a row's remove (the hover 🗑). */
export function ConfirmRemove({
  title,
  description,
  confirmLabel,
  pending,
  onConfirm,
  onClose,
}: {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={pending}
            className={cn(pending && "opacity-70")}
            onClick={onConfirm}
          >
            {pending ? "Working…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
